import type { EntryPoint, EntryPointComponent } from 'react-relay';
/* eslint-disable eslint-comments/no-restricted-disable */
/* eslint-disable @atlassian/react-entrypoint/no-imports-in-entrypoint-file */
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { getProjectKeyId } from '@jira/router-resources__utils/src/getProjectKeyId.tsx';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { JiraProjectAri } from '@atlassian/ari/jira/project';
import parameters from './ui/__generated__/HorizontalNavJiraInnerQuery$parameters';
import type { HorizontalNavJiraInnerQuery } from './ui/__generated__/HorizontalNavJiraInnerQuery.graphql';

const NAVIGATION_ITEMS_PAGE_SIZE = 75;

type JiraHorizontalNavEntrypointComponent = EntryPointComponent<
	{
		horizontalNavQuery: HorizontalNavJiraInnerQuery;
	},
	{},
	{},
	{}
>;

export const jiraHorizontalNavEntrypoint: EntryPoint<
	JiraHorizontalNavEntrypointComponent,
	EntryPointRouteParams
> = createEntryPoint({
	root: JSResourceForUserVisible<JiraHorizontalNavEntrypointComponent>(
		/* webpackChunkName: "async-jira-horizontal-nav-entrypoint" */ './ui/HorizontalNavJiraInner.tsx',
		{
			entryFsSsrLiftImportToModule: true,
			includeInShellOnlySsrBundle: true,
		},
	),
	getPreloadProps: ({
		context: { match, query },
		tenantContext: { cloudId },
	}: EntryPointRouteParams) => {
		let boardId = match.params.boardId;
		const { projectKey, projectId } = getProjectKeyId(match, query);

		const projectIdOrKey = projectId || projectKey;

		const isTMPBoardRoute = boardId ? match.url.includes('/software/projects/') : false;
		if (!boardId) {
			// If there is no boardId in the URL then we should check to see if it is provided as part of
			// the Connect app rapidView query param. This has been added to address JRACLOUD-94629
			const rapidViewBoard = query.rapidView ? query.rapidView : null;
			boardId = rapidViewBoard || boardId || null;
		}

		const boardAri = boardId ? `ari:cloud:jira-software:${cloudId}:board/${boardId}` : '';

		const projectAri = projectId
			? JiraProjectAri.create({
					siteId: cloudId,
					projectId,
				}).toString()
			: '';

		let containerNavigationInput;
		if (isTMPBoardRoute) {
			containerNavigationInput = projectAri
				? { scopeId: boardAri }
				: { projectKeyQuery: { cloudId, projectKey } };
		} else {
			containerNavigationInput =
				boardAri || projectAri
					? { scopeId: boardAri || projectAri }
					: { projectKeyQuery: { cloudId, projectKey } };
		}

		return {
			queries: {
				horizontalNavQuery: {
					options: {
						fetchPolicy: 'store-and-network' as const,
					},
					parameters,
					variables: {
						cloudId,
						projectIdOrKey,
						withProject: Boolean(projectIdOrKey),
						boardId: boardAri,
						withBoard: Boolean(boardAri),
						containerNavigationInput,
						isTMPBoardRoute,
						first: NAVIGATION_ITEMS_PAGE_SIZE,
					},
				},
			},
		};
	},
});
